import {
  useEffect,
  useRef,
  RefObject,
  useCallback,
  useLayoutEffect,
} from 'react';
import isNil from 'lodash/isNil';
import {
  isEmptyString,
} from '@populateui/utils';
import last from 'lodash/last';

export type AutoFocusOptions = {
  enabled?: boolean;
  delay?: number;
  skipIfValueExists?: boolean;
  inputSelector?: string;
};

const defaultInputSelector = 'input:not([disabled]):not([readonly]):not([type="hidden"]), textarea:not([disabled]):not([readonly]), select:not([disabled])';

export function useFormAutoFocus<T extends HTMLElement = HTMLInputElement>(
  options: AutoFocusOptions = {},
  dependencies: (string | number | boolean)[] = [],
): RefObject<T> {
  const {
    enabled = true,
    delay = 0,
    skipIfValueExists = true,
    inputSelector = defaultInputSelector,
  } = options;

  const ref = useRef<T>(null!);

  const focusFirstInput = useCallback(() => {
    const isEmptyValue = isEmptyString((ref.current as any)?.value);
    const skip = skipIfValueExists && !isEmptyValue;

    const dialog = last(document.querySelectorAll('[role="dialog"]'));
    const focusFromRef = !isNil(ref.current) && !skip;
    const focusFromDialog = !skip && !isNil(dialog);

    if (focusFromRef) {
      ref?.current?.focus();
    } else if (focusFromDialog) {
      const inputs = Array.from(dialog.querySelectorAll(inputSelector)) as HTMLElement[];
      const firstInput = inputs.find((input) => !skipIfValueExists || !isNil((input as any).value));
      firstInput?.focus();
    }
  }, [ref, skipIfValueExists, inputSelector]);

  useEffect(() => {
    if (enabled) {
      setTimeout(focusFirstInput, delay);
    }
  }, [enabled, delay, focusFirstInput, ...dependencies]);

  return ref;
}

export function useEvent<T extends(...args: any[]) => any>(fn: T): T {
  const ref = useRef(fn);

  // layout effect avoids a “tear” between render and effect in most UI cases
  useLayoutEffect(() => {
    ref.current = fn;
  }, [fn]);

  return useCallback(((...args: any[]) => ref.current(...args)) as T, []);
}
